import React from 'react';
import { X, ShieldCheck, Lock, AlertTriangle, EyeOff, UserCheck, PhoneCall, CheckCircle } from 'lucide-react';

interface SafetyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SafetyModal: React.FC<SafetyModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/75 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 animate-in fade-in">
      <div className="relative bg-white rounded-3xl max-w-2xl w-full overflow-hidden shadow-2xl border border-pink-100 p-6 sm:p-8 max-h-[90vh] overflow-y-auto">
        
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center">
            <ShieldCheck className="w-7 h-7" />
          </div>
          <div>
            <h3 className="text-2xl font-extrabold text-slate-900">Safety & Trust Standards</h3>
            <p className="text-xs text-slate-500">PairUpToday Zero-Tolerance Safety Protocol & Code of Conduct</p>
          </div>
        </div>

        <div className="space-y-4 text-xs sm:text-sm text-slate-600">
          
          <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100 space-y-2">
            <h4 className="font-bold text-slate-900 text-sm flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              1. 100% Government ID Verified Profiles
            </h4>
            <p className="text-slate-600 leading-relaxed text-xs">
              Every companion and user undergoes mandatory biometric Aadhaar / Passport verification, criminal background checks, and an in-person behavioral interview before getting listed.
            </p>
          </div>

          <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100 space-y-2">
            <h4 className="font-bold text-slate-900 text-sm flex items-center gap-2">
              <Lock className="w-4 h-4 text-pink-500" />
              2. Strict Public Place Policy
            </h4>
            <p className="text-slate-600 leading-relaxed text-xs">
              All initial meetups must take place in public venues (restaurants, cafes, banquet halls, theatres, or corporate venues). Private residential meetings are prohibited without mutual consent and safety registration.
            </p>
          </div>

          <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100 space-y-2">
            <h4 className="font-bold text-slate-900 text-sm flex items-center gap-2">
              <EyeOff className="w-4 h-4 text-purple-500" />
              3. Complete Privacy & Anonymity
            </h4>
            <p className="text-slate-600 leading-relaxed text-xs">
              Your real contact details and phone numbers are never disclosed. All communication is routed through encrypted platform channels.
            </p>
          </div>

          <div className="p-4 rounded-2xl bg-pink-50 border border-pink-200 space-y-2">
            <h4 className="font-bold text-pink-900 text-sm flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-pink-600" />
              4. 100% Escrow & Meet Completion OTP Policy
            </h4>
            <p className="text-pink-950 leading-relaxed text-xs">
              Client payments remain safely locked inside an encrypted Escrow vault. Funds are only released to the companion's verified account after both parties have met in person and the client shares their secret 4-digit completion OTP.
            </p>
          </div>

          <div className="p-4 rounded-2xl bg-emerald-50 border border-emerald-200 space-y-2">
            <h4 className="font-bold text-emerald-900 text-sm flex items-center gap-2">
              <PhoneCall className="w-4 h-4 text-emerald-600" />
              5. 24/7 Dedicated SOS Support Hotline
            </h4>
            <p className="text-emerald-800 leading-relaxed text-xs">
              Emergency assistance hotline: <span className="font-bold">+91 1800-PAIRUP (724787)</span>. Real-time emergency check-ins are available for all confirmed bookings.
            </p>
          </div>

          <div className="p-4 rounded-2xl bg-indigo-50 border border-indigo-200 space-y-2">
            <h4 className="font-bold text-indigo-950 text-sm flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-indigo-600" />
              6. Privacy-First & Strict Consent-Only Permissions
            </h4>
            <p className="text-indigo-900 leading-relaxed text-xs">
              PairUpToday strictly enforces a Zero-Surveillance and Just-In-Time consent architecture. No location, camera, microphone, or device data is ever accessed in the background or without your explicit prompt. You retain full control to inspect, grant, or revoke permissions at any time.
            </p>
          </div>

        </div>

        <div className="mt-6 pt-4 border-t border-slate-100 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl shadow-sm"
          >
            I Understand & Agree
          </button>
        </div>

      </div>
    </div>
  );
};
